/**
 * 나의 할일 화면 모델 — 서버 응답(@indipia/shared TodoItem · WeekDay)을 화면 줄로 옮긴다.
 * 할일 제목 · 사유 · 버튼 · 이동 주소 · NEW 는 서버(TODO-01)가 할일 정의(TM_SITE_TODO)로 완성해 내려준 값을 그대로 쓴다.
 * 화면은 할일 코드로 분기하거나 문구 · 주소를 만들지 않는다(S14-S15 구현설계서 8장). 할일 코드는 화면에 보이지 않는다.
 *  - 처리 버튼: actionTypeCode 가 ROUTE 면 webPath 로 이동, API 면 actionUrl 호출 뒤 다시 조회. actionName 이 null 이면 버튼 없음.
 *  - 줄 클릭: viewPath 로 이동. null 이면 줄을 누를 수 없다.
 */
import type { TodoItem as ServerTodo, TodoMoreAction, WeekDay as ServerWeekDay } from '@indipia/shared';

export interface TodoItem {
  id: string;
  level: 'r' | 'y';
  /** 할일 제목 */
  title: string;
  /** 짧은 제목 — 폭이 좁은 곳(7일 탭 · 배너 요약) */
  abbr: string | null;
  site: string;
  siteId: number;
  taskId: number;
  /** 공종명 */
  task: string;
  /** 사유 문장 */
  reason: string;
  /** 처리 버튼 문구(없으면 null — 버튼 없음) */
  action: string | null;
  /** 처리 동작 유형 — ROUTE 화면 이동 · API 바로 실행 */
  actionType: string | null;
  /** 처리 버튼 이동 주소(ROUTE) */
  href: string | null;
  /** 처리 버튼 호출 주소(API) */
  actionUrl: string | null;
  /** 반장님 전화(웹에서는 비활성) — 없으면 null */
  phone: TodoMoreAction | null;
  /** 현장 담당자·관리자 회원 ID (담당자 필터 기준) */
  ownerIds: number[];
  /** 표시용 담당자 이름(쉼표 구분) */
  owner: string;
  /** 줄 클릭 이동 주소 — null 이면 줄이 클릭되지 않는다 */
  rowHref: string | null;
  /** 줄 툴팁 */
  destLabel: string;
  /** 오늘 알림 시각 이후 생긴 할일 */
  isNew: boolean;
}

/** 처리 버튼을 누를 수 있는지 — 화면 이동이면 주소가, 바로 실행이면 호출 주소가 있어야 한다 */
export function hasAction(t: Pick<TodoItem, 'action' | 'actionType' | 'href' | 'actionUrl'>): boolean {
  if (!t.action) return false;
  return t.actionType === 'API' ? !!t.actionUrl : !!t.href;
}

const ROW_LABEL = '자세히 보기';

export interface TodoMapContext {
  /** 회원 ID → 이름 */
  memberNames: ReadonlyMap<number, string>;
}

export function toTodoItem(t: ServerTodo, c: TodoMapContext): TodoItem {
  const owner = t.ownerMemberIds.map((id) => c.memberNames.get(id)).filter((n): n is string => !!n).join(', ');
  return {
    id: `${t.taskId}:${t.signalTypeCode}:${t.todoCode}`,
    level: t.level,
    title: t.todoName ?? '',
    abbr: t.todoAbbrName ?? null,
    site: t.siteName ?? `현장 ${t.siteId}`,
    siteId: t.siteId,
    taskId: t.taskId,
    task: t.workTypeName ?? '',
    reason: t.todoReasonText ?? '',
    action: t.actionName ?? null,
    actionType: t.actionTypeCode ?? null,
    href: t.webPath ?? null,
    actionUrl: t.actionUrl ?? null,
    phone: t.moreActions?.find((m) => !!m.tel) ?? null,
    ownerIds: t.ownerMemberIds,
    owner,
    rowHref: t.viewPath ?? null,
    destLabel: t.viewPath ? ROW_LABEL : '',
    isNew: t.isNew === true,
  };
}

// ── 7일 ───────────────────────────────────────────────────────────────

/** 7일 일정 종류 → 표시 이름 (입고 · 착공 · 공사완료) */
const EVENT_LABEL: Record<string, string> = { DELIVERY: '입고', START: '착공', END: '공사완료' };

/** 7일 탭 줄 — 오늘 할일(todo) 또는 일정(event). 일정 줄은 누를 수 없다(이동 주소 없음). */
export type WeekEntry =
  | { kind: 'todo'; id: string; site: string; siteId: number; todo: TodoItem }
  | { kind: 'event'; id: string; site: string; siteId: number; type: string; label: string; task: string };

export interface WeekDayView {
  /** 'YYYY-MM-DD' */
  date: string;
  /** 'M/D(요일)' */
  label: string;
  today: boolean;
  items: WeekEntry[];
}

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const w = new Date(y, m - 1, d).getDay();
  return `${m}/${d}(${WEEKDAY[w]})`;
}

/**
 * 7일 날짜 카드 — 서버 weekDays(입고 · 착공 · 공사완료) 날짜 순 그대로, 첫 날(오늘) 카드 맨 위에 오늘 할일을 넣는다.
 * 내일 이후 날짜의 예정 할일은 서버가 정하기 전이라 없다(설계서 10장 결정 2).
 */
export function toWeekDays(days: ServerWeekDay[] | undefined, todayTodos: TodoItem[]): WeekDayView[] {
  return (days ?? []).map((d, i) => {
    const events: WeekEntry[] = d.sites.flatMap((s) => s.events.map((e, j) => ({
      kind: 'event' as const,
      id: `${d.date}:${s.siteId}:${e.type}:${e.taskId}:${j}`,
      site: s.siteName ?? `현장 ${s.siteId}`,
      siteId: s.siteId,
      type: e.type,
      label: EVENT_LABEL[e.type] ?? e.type,
      task: e.workTypeName ?? '',
    })));
    const todos: WeekEntry[] = i === 0
      ? todayTodos.map((t) => ({ kind: 'todo' as const, id: `todo:${t.id}`, site: t.site, siteId: t.siteId, todo: t }))
      : [];
    return { date: d.date, label: dayLabel(d.date), today: i === 0, items: [...todos, ...events] };
  });
}
